/**
 * ContractsAllListPage — UNION view local_p + consignment_p (NPE).
 * UX/UI đồng bộ ContractListView (TableVirtuoso + popover filter + TablePagination).
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListAllContractsQuery } from '@/store/api/contractsAllApi'
import type { ContractAllItem } from '@/types/api'

type FilterKind = 'search' | 'source' | 'certification'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date'
type ColumnDef = { key: keyof ContractAllItem; label: string; kind?: ColumnKind }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Số HĐ' },
  { key: 'contract_date', label: 'Ngày HĐ', kind: 'date' },
  { key: 'short_name', label: 'Vendor' },
  { key: 'cp_code', label: 'CP Code' },
  { key: 'license_cert_no', label: 'License' },
  { key: 'certification', label: 'Cert' },
  { key: 'source', label: 'Nguồn' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`.slice(-2)
  return `${day}-${month}-${year}`
}

const distinctValues = (rows: ContractAllItem[], key: keyof ContractAllItem): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowMatchesSearch = (row: ContractAllItem, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    const value = row[col.key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

const cellValue = (row: ContractAllItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  return String(val)
}

export function ContractsAllListPage() {
  const navigate = useNavigate()
  const snack = useSnackbar()

  const [selectedContractNo, setSelectedContractNo] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [certFilter, setCertFilter] = useState('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) =>
    setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || sourceFilter || certFilter)
  const clearAllFilters = () => {
    setSearchTerm('')
    setSourceFilter('')
    setCertFilter('')
    setPage(0)
  }

  const { data, isLoading, isFetching, error } = useListAllContractsQuery({
    page: 1,
    page_size: ALL_ROWS_PAGE_SIZE,
  })

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        if (sourceFilter && row.source !== sourceFilter) return false
        if (certFilter && row.certification !== certFilter) return false
        return true
      }),
    [baseRows, searchTerm, sourceFilter, certFilter],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  const sourceOptions = useMemo(() => distinctValues(baseRows, 'source'), [baseRows])
  const certOptions = useMemo(() => distinctValues(baseRows, 'certification'), [baseRows])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const selectedRow = useMemo<ContractAllItem | null>(
    () => baseRows.find((r) => r.contract_no === selectedContractNo) ?? null,
    [baseRows, selectedContractNo],
  )

  const handleView = () => {
    if (!selectedRow) return
    const no = selectedRow.contract_no
    if (no.startsWith('P-')) {
      navigate(`/purchase/local-p/${encodeURIComponent(no)}`)
      return
    }
    if (no.startsWith('NPE-')) {
      snack.info('NPE detail page sẽ có ở Sprint 4 — tạm điều hướng qua Local P.')
      navigate(`/purchase/local-p/${encodeURIComponent(no)}`)
      return
    }
    snack.warning(`Không xác định loại hợp đồng cho ${no}`)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={sourceFilter ? 'contained' : 'outlined'} color={sourceFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('source', e)} sx={filterBarBtnSx}>Source</Button>
          <Button size="small" variant={certFilter ? 'contained' : 'outlined'} color={certFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('certification', e)} sx={filterBarBtnSx}>Certification</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleView}>View</Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      <Alert severity="info" sx={{ mb: 0.5 }}>
        UNION view: Local Purchase (P-...) và Consignment / NPE (NPE-...). NPE detail Sprint 4.
      </Alert>

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load contracts.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' }, '& .MuiTablePagination-select': { fontSize: '0.72rem' } }}
            />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Số HĐ, vendor..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'source' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Source</InputLabel>
              <Select label="Source" value={sourceFilter} onChange={(e) => { setSourceFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {sourceOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'certification' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Certification</InputLabel>
              <Select label="Certification" value={certFilter} onChange={(e) => { setCertFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {certOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: ContractAllItem) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1100, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowContractNo = Number.isFinite(idx) ? rows[idx]?.contract_no : null
                return (
                  <TableRow {...props} hover selected={rowContractNo != null && rowContractNo === selectedContractNo} sx={{ cursor: 'pointer' }} onClick={() => { if (rowContractNo != null) setSelectedContractNo(rowContractNo) }} />
                )
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ContractsAllListPage
